import axios from 'axios';
import { loginRequest, msalInstance } from '../../config/authConfig';
import router from '../../router';

class AuthenticationService {
    private static tokenPromise: Promise<string> | null = null;
    private static isInteractionInProgress: boolean = false;

    public initialize(azureADClientId: string, baseApiUrl: string) {
        this.configureAxios(azureADClientId, baseApiUrl);
    }

    public handleAccessDenied() {
        const error = this.getUrlHashParameterByName('error');
        if (error === 'access_denied') {
            router.push({ name: 'notallowed' });
        }
    }

    public clearInteractionFlag() {
        AuthenticationService.isInteractionInProgress = false;
    }

    private configureAxios(azureADClientId: string, baseApiUrl: string): void {
        axios.defaults.baseURL = baseApiUrl;
        axios.defaults.headers = {
            Authorization: null
        };

        if (msalInstance.getAllAccounts().length > 0) {
            // set the Headers, don't wait for a 401 to trigger this
            AuthenticationService.getToken(azureADClientId);
        }

        axios.interceptors.response.use(
            response => response,
            async error => {
                if (error.response.status === 401) {
                    // try fetching new token and replay request
                    await AuthenticationService.getToken(azureADClientId);
                    let config = error.response.config;
                    config.headers.Authorization = axios.defaults.headers.Authorization;
                    let result = await axios({
                        method: config.method,
                        url: config.url,
                        data: config.data,
                        headers: {
                            Accept: config.headers.Accept,
                            Authorization: config.headers.Authorization,
                            'Content-Type': config.headers['Content-Type']
                        }
                    });
                    return result;
                } else if (error.response.status === 403) {
                    router.push({ name: 'notallowed' });
                } else if (error.response.status === 404) {
                    router.push({ name: 'notfound' });
                } else {
                    return Promise.reject(error);
                }
            }
        );
    }

    private static getToken(resource: string): Promise<string> {
        // If there's already a token request in progress, return that promise
        if (AuthenticationService.tokenPromise) {
            return AuthenticationService.tokenPromise;
        }

        AuthenticationService.tokenPromise = new Promise(async (resolve, reject) => {
            try {
                const account = msalInstance.getActiveAccount();
                if (!account) {
                    // If no active account, try to get one from cache
                    const accounts = msalInstance.getAllAccounts();
                    if (accounts.length === 0) {
                        // No accounts found, redirect to login
                        if (!AuthenticationService.isInteractionInProgress) {
                            AuthenticationService.isInteractionInProgress = true;
                            try {
                                await msalInstance.loginRedirect(loginRequest);
                            } catch (error) {
                                AuthenticationService.isInteractionInProgress = false;
                                throw error;
                            }
                        }
                        reject(new Error('No accounts found, redirecting to login'));
                        return;
                    }
                    msalInstance.setActiveAccount(accounts[0]);
                }

                const tokenRequest = {
                    scopes: [resource],
                    account: msalInstance.getActiveAccount()
                };

                const response = await msalInstance.acquireTokenSilent(tokenRequest);
                axios.defaults.headers.Authorization = `BEARER ${response.accessToken}`;
                resolve(response.accessToken);
            } catch (error: any) {
                if (error.name === 'InteractionRequiredAuthError') {
                    // Need user interaction - this will trigger a redirect
                    if (!AuthenticationService.isInteractionInProgress) {
                        AuthenticationService.isInteractionInProgress = true;
                        try {
                            await msalInstance.acquireTokenRedirect({
                                scopes: [resource],
                                account: msalInstance.getActiveAccount()
                            });
                            // This code won't execute due to redirect
                        } catch (redirectError) {
                            AuthenticationService.isInteractionInProgress = false;
                            reject(new Error('Failed to acquire token'));
                        }
                    } else {
                        reject(new Error('Interaction already in progress'));
                    }
                } else if (error.name === 'BrowserAuthError' && error.errorMessage?.includes('interaction_in_progress')) {
                    // Another interaction is already in progress, wait and retry
                    setTimeout(() => {
                        AuthenticationService.getToken(resource).then(resolve).catch(reject);
                    }, 1000);
                    return;
                } else {
                    // Other error, try to login
                    if (!AuthenticationService.isInteractionInProgress) {
                        AuthenticationService.isInteractionInProgress = true;
                        try {
                            await msalInstance.loginRedirect(loginRequest);
                        } catch (loginError) {
                            AuthenticationService.isInteractionInProgress = false;
                            reject(new Error('Failed to acquire token'));
                        }
                    } else {
                        reject(new Error('Interaction already in progress'));
                    }
                }
            } finally {
                // Clear the promise so new requests can be made
                AuthenticationService.tokenPromise = null;
            }
        });

        return AuthenticationService.tokenPromise;
    }

    private getUrlHashParameterByName(name: string) {
        // don't judge me, I copied this from stack overflow
        let hashParams: any = {};
        let a = /\+/g; // Regex for replacing addition symbol with a space
        let r = /([^&;=]+)=?([^&;]*)/g;
        let d = (s: string) => {
            return decodeURIComponent(s.replace(a, ' '));
        };
        let q = window.location.hash.substring(2);

        let e = r.exec(q);
        while (e) {
            hashParams[d(e[1])] = d(e[2]);
            e = r.exec(q);
        }

        return hashParams[name];
    }
}

export const authenticationService = new AuthenticationService();
